import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ShoppingCart, CaretDown, CaretUp } from "@phosphor-icons/react";

/**
 * Recipe → Shopping List drawer.
 *
 * Rendered above the ingredient list on RecipeView. Collapsed by default —
 * one big [Add to shopping list] button. When expanded, users tick which
 * ingredients they want and hit "Add N items". Servings scaling is
 * respected (uses the current `scale` from RecipeView).
 *
 * Merging + aisle classification happens server-side. After a successful
 * add the drawer shows a "View shopping list →" cross-link.
 */
export default function AddToShoppingListDrawer({ recipe, scale = 1, cookbookSlug = null }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Default: all ingredients selected. Users untick the ones they already
  // have. Using indices (not names) so duplicate ingredient names still
  // toggle independently.
  const [selected, setSelected] = useState(
    () => new Set((recipe?.ingredients || []).map((_, i) => i))
  );
  const [addedCount, setAddedCount] = useState(0);

  const toggle = (i) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set((recipe?.ingredients || []).map((_, i) => i)));
  const selectNone = () => setSelected(new Set());

  const addToList = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const items = [...selected].map((i) => {
        const ing = recipe.ingredients[i];
        return {
          name: ing.name,
          quantity: ing.quantity ?? null,
          unit: ing.unit || "",
          notes: ing.notes || "",
        };
      });
      const { data } = await api.post("/shopping-list/add-from-recipe", {
        recipe_id: recipe.recipe_id,
        recipe_title: recipe.title,
        cookbook_slug: cookbookSlug,
        servings_multiplier: scale,
        ingredients: items,
      });
      const added = (data?.added || 0) + (data?.merged || 0);
      const staples = data?.skipped_staples || 0;
      setAddedCount(added);
      let msg = `Added ${added} ${added === 1 ? "item" : "items"} to your shopping list`;
      if (staples > 0) msg += ` · skipped ${staples} pantry ${staples === 1 ? "item" : "items"}`;
      toast.success(msg);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add to list");
    } finally {
      setBusy(false);
    }
  };

  const total = recipe?.ingredients?.length || 0;
  if (!total) return null;

  return (
    <div className="mb-2" data-testid="add-to-shopping-drawer">
      {!open ? (
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="open-add-to-shopping"
            onClick={() => setOpen(true)}
            className="btn-orange !py-2.5 inline-flex items-center gap-2"
          >
            <ShoppingCart size={18} weight="bold" /> Add to shopping list
            <CaretDown size={14} weight="bold" />
          </button>
          {addedCount > 0 && (
            <Link
              to="/shopping"
              data-testid="view-shopping-list"
              className="text-sm font-bold text-rf-orange hover:underline"
            >
              View shopping list →
            </Link>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-rf-ink/10 bg-rf-cream-alt/40 p-4" data-testid="shopping-drawer-open">
          <div className="flex items-center justify-between mb-3">
            <p className="font-display font-black text-lg text-rf-ink">Choose what to buy</p>
            <button onClick={() => setOpen(false)} className="text-rf-ink-soft hover:text-rf-ink text-sm inline-flex items-center gap-1">
              Close <CaretUp size={13} weight="bold" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            <button data-testid="drawer-select-all" onClick={selectAll} className="chip !bg-white !text-rf-ink !border !border-rf-ink/15">Select all</button>
            <button data-testid="drawer-select-none" onClick={selectNone} className="chip !bg-white !text-rf-ink !border !border-rf-ink/15">Deselect all</button>
            <span className="text-rf-ink-soft ml-auto self-center">
              Serves scaled × {scale}
            </span>
          </div>
          <ul className="max-h-72 overflow-y-auto space-y-1 mb-3">
            {recipe.ingredients.map((ing, i) => {
              const q = ing.quantity ? +(ing.quantity * scale).toFixed(2) : ing.quantity;
              const checked = selected.has(i);
              return (
                <li key={i} className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(i)}
                    className="mt-1 w-4 h-4 accent-rf-orange shrink-0 cursor-pointer"
                    data-testid={`drawer-tick-${i}`}
                    aria-label={`Add ${ing.name} to shopping list`}
                  />
                  <p className="text-sm text-rf-ink flex-1 leading-snug">
                    {q ? <span className="font-bold">{q}{ing.unit ? ` ${ing.unit}` : ""} </span> : null}
                    {ing.name}
                    {ing.notes && <span className="text-rf-ink-soft"> — {ing.notes}</span>}
                  </p>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-rf-ink-soft">
              {selected.size} of {total} selected · items you always have (marked as pantry) are auto-skipped.
            </p>
            <button
              data-testid="drawer-add-btn"
              onClick={addToList}
              disabled={busy || selected.size === 0}
              className="btn-orange !py-2 !px-4 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShoppingCart size={16} weight="bold" />
              {busy ? "Adding…" : `Add ${selected.size} ${selected.size === 1 ? "item" : "items"}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
